import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Artikel, ArtikelSeite, Steuersatz,
              Unternehmen } from "@kundeninfo/api-client";
import { Objektliste, Objektzeile } from "@kundeninfo/ui";
import { ArtikelDetail } from "../components/ArtikelDetail.js";
import { artikelMeta, quellenMarke } from "../format.js";
import { Filterkopf } from "../components/Filterkopf.js";
import { KiKnopf } from "../components/meHuellen.js";
import { Auswahl } from "@kundeninfo/ui/me-design";
import "./BereichArtikel.css";

/**
 * Der Artikel-BEREICH: Spaltenliste links (Filterkopf, Artikelzeilen, Blätterung),
 * vollwertiges Lese-Detail rechts — die Schwester von views/BereichAngebote.tsx und
 * views/BereichAuftraege.tsx und Zeile für Zeile nach demselben Muster gebaut. Was dort
 * steht, gilt hier gleichlautend; deshalb nennt dieser Kopf nur, was den ARTIKEL
 * unterscheidet.
 *
 * Die ANORDNUNG ist zweimal gewechselt und steht jetzt wieder, wo sie anfing: Spalte und
 * Detail bis zum 07.09.2026, dann „Filterkopf statt zweiter Spalte" (alles untereinander in
 * voller Breite), und seit dem 23.09.2026 wieder Spalte und Detail (Entscheid Dirk am
 * Live-System). Vom Umbau des 07.09. BLEIBT, was sich dabei als richtig erwiesen hat: der
 * Filterkopf als EIN Baustein für alle vier Bereiche (er steht jetzt oben IN der Spalte,
 * siehe `lage="spalte"`), der Filter in der Adresse und die Detail-Bausteine. Was geht, ist
 * allein das Untereinander — und mit ihm der bereichseigene Sprung-Mechanismus
 * (components/Bereichsfaltung.tsx), denn die Faltung auf schmalem Gerät macht wieder der
 * Rahmen.
 *
 * Die ZEILEN zeichnet seit dem 23.09.2026 der gemeinsame Baustein `Objektliste`/
 * `Objektzeile` aus @kundeninfo/ui — dieselbe Zeile wie im Artikelstamm der Verwaltung, und
 * das ist der Punkt: Es ist derselbe Artikel.
 *
 * Er löst die bisherige Katalog-Ansicht ab (views/Artikelkatalog.tsx, seit dem 05.09.
 * hinter der Adresse `/artikel`). Die Adresse bleibt, der Umfang bleibt: Der zugrunde
 * liegende Weg `GET /api/artikel` (Ruling P3B1B-G) kann ausschliesslich lesen und gibt
 * ausschliesslich AKTIVE Artikel heraus. Ein stillgelegter Artikel taucht hier gar nicht
 * erst auf, und es gibt auch keinen Schalter, der ihn hervorholte.
 *
 * **Warum ein Hook und keine Komponente.** Wie bei den Schwesterbereichen: Der Bereich
 * füllt ZWEI Bereiche des Rahmens (`MeSchale.list` und `MeSchale.detail`), die sich einen
 * Zustand teilen. Eine Komponente kann ihre Ausgabe nicht auf zwei Eigenschaften des
 * Rahmens verteilen; zwei Komponenten müssten den Zustand irgendwo darüber halten, und
 * „irgendwo darüber" wäre App.tsx.
 *
 * **Der Artikel ist KUNDENLOS.** Damit entfallen gegenüber T3/T4 zwei Stücke, und zwar
 * ersatzlos: der Verweis „Zum Kunden" und die Kundenauswahl als erster Schritt des
 * Anlegens. ANGELEGT wird hier ohnehin nicht — das bleibt wie bisher allein der Verwaltung
 * vorbehalten (Artikelstamm), und dorthin führt für ADMINs der bestehende Verweis „Pflege
 * in der Verwaltung" im Filterkopf. Was der ADMIN hier bekommt, ist der Bestands-Kurzweg
 * „Ändern" am geöffneten Artikel; gewächtert bleibt der Schreibweg
 * (`PUT /api/verwaltung/artikel/…`) auf dem SERVER.
 *
 * **Die Trefferzeile IST der volle Satz.** Anders als bei Angebot und Auftrag, wo die
 * Trefferzeile die magere Gestalt eines Sucheinstiegs hat und das Detail den Vorgang
 * eigens nachliest: `searchProducts` liefert vollständige `Artikel` samt `version`. Es gibt
 * darum keinen zweiten Abruf und keinen Kundenbezug aufzulösen — wohl aber dieselbe
 * dreistufige Auskunft für ein Lesezeichen auf einen Artikel, den die aktuelle Trefferseite
 * nicht enthält (siehe `satz` unten).
 *
 * **Der offene Zustand liegt im BAUSTEIN, nicht hier.** Der Änderungsdialog gehört
 * components/ArtikelDetail.tsx (Etappe 1, Aufgabe 2) und wird von dort geöffnet und
 * geschlossen — der Bereich sieht ihn nicht und fasst den Baustein dafür auch nicht an. Die
 * Regeln aus Aufgabe 3 gelten trotzdem, nur werden sie hier anders eingelöst:
 *
 *  - *Kein Zustand, der den Adresswechsel überlebt.* Der Dialog hängt am `key` des
 *    geöffneten Artikels (siehe unten): Wechselt die Adresse auf einen anderen Artikel,
 *    bekommt der Baustein eine neue Identität und der Dialog ist zu. Verlässt man den
 *    Bereich, hängt App.tsx das Detail ganz aus — derselbe Reset, nur vom Rahmen erledigt.
 *  - *Kein Neuladen unter einer offenen Bearbeitung.* Der Dialog ist MODAL und seit dem
 *    06.09.2026 STRIKT: Sein Hintergrund fängt jeden Klick daneben ab und lässt ihn ins
 *    Leere laufen (components/ArtikelDialog — geschlossen wird nur noch über Speichern
 *    oder Abbrechen), die Objektliste ist währenddessen also gar nicht bedienbar. Sie
 *    deshalb zusätzlich zu SPERREN wäre hier falsch: Der Artikeldialog arbeitet — anders
 *    als die Vorgangsdetails — ohne Versionsvergleich (last-write-wins, siehe sein
 *    Kopfkommentar), es gibt also keine optimistische Sperre, die ein Neuladen aushebeln
 *    könnte.
 *  - *Kein Verlassen-Wächter des Routers.* Ihn zu registrieren setzte voraus, dass der
 *    Bereich vom offenen Dialog WEISS — das ginge nur über eine neue Rückmeldung im
 *    Baustein, und der bleibt in dieser Aufgabe unangetastet. Gegen den Verlust
 *    ungespeicherter Eingaben trägt der Dialog seinen eigenen Wächter (`useBeforeUnload`).
 *
 * **CSS:** eigener Block `bart__` für das Drumherum. Was der Baustein mitbringt, behält
 * seine Klassen (`akat__` — herausgelöste Bestandsteile werden nicht umbenannt, Etappe 1,
 * Aufgabe 2); geteilt wird über Ansichten hinweg KEIN Block.
 */

/** Die zwei Lesewege des Bereichs und der EINE Schreibweg des ADMIN-Kurzwegs — so lässt er
 *  sich im Test mit drei vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen. */
export type BereichArtikelClient =
  Pick<ApiClient, "searchProducts" | "listActiveTaxRates" | "updateProduct">;

export type BereichArtikelProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Der Hook wird — Regeln der Hooks — auch
   *  an anderen Adressen gerufen; ohne diesen Riegel liefe seine Suche überall mit. */
  aktiv: boolean;
  client: BereichArtikelClient;
  /** Die sichtbaren Konzernunternehmen: Auswahlliste des Filters und Auflösung im Dialog. */
  companies: Unternehmen[];
  /** Der geöffnete Artikel aus der Adresse (`/artikel/:id`) — `null` heisst „keiner". */
  artikelId: string | null;
  /**
   * Die Zeile, die die Liste als gewählt zeigt, solange KEIN Objekt offen ist — am Telefon
   * die zuletzt geöffnete, damit der Rückweg die Auswahl behält (App.tsx, Hausregel
   * „Scroll bei Neuzeichnen"). Weggelassen: keine.
   */
  markiertId?: string | null;
  /**
   * Zeigt den Verweis „Pflege in der Verwaltung" UND (über den Baustein) den Ändern-Knopf.
   * Nur für ADMIN — für alle anderen sind Verwaltung wie Schreibweg mit 403 verschlossen
   * (der Server prüft das, nicht diese Zeile); ein Knopf, der nur in eine sichere Sackgasse
   * führte, hätte hier nichts verloren.
   */
  istAdmin: boolean;
  /** Adresswechsel innerhalb des Bereichs. Der Bereich navigiert NICHT selbst — die Adresse
   *  kennt App.tsx (dasselbe Muster wie in den Schwesterbereichen). */
  onOeffnen: (artikelId: string | null) => void;
  /** In den Artikelstamm der Verwaltung (nur ADMIN) — mit Herkunfts-Rückweg. */
  onPflege: () => void;
};

export type BereichArtikelTeile = {
  /**
   * Für `MeSchale.list` — Filterkopf, Artikelliste, Blätterung.
   *
   * Sie war zwischen dem 07. und dem 23.09.2026 nicht da: Der Bereich lieferte ein einziges
   * Stück `inhalt` in voller Breite, mit der Liste über dem Detail. Dirk hat das am
   * Live-System zurückgenommen (23.09.2026: „die Liste dann als Spalte anzeigen und die
   * Übersicht als Inhaltsbereich") — die Anordnung, die der Bereich schon bis zum 07.09.
   * hatte.
   */
  spalte: ReactElement;
  /** Für `MeSchale.detail` — der gewählte Artikel als Lese-Detail, in seiner Karte. */
  detail: ReactElement;
};

/** Wie viele Artikel auf eine Seite gehen — dieselbe Zahl wie bisher im Katalog und in der
 *  Verwaltung, damit Bereich und Pflegesicht gleich blättern. Bewusst kleiner als die
 *  Servergrenze (200). */
const JE_SEITE = 20;

export function useBereichArtikel(p: BereichArtikelProps): BereichArtikelTeile {
  const { client, companies, aktiv, artikelId, istAdmin } = p;

  const [seite, setSeite] = useState<ArtikelSeite | null>(null);
  const [steuersaetze, setSteuersaetze] = useState<Steuersatz[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [suche, setSuche] = useState("");
  const [offset, setOffset] = useState(0);
  /** Hochgezählt, wenn die Trefferseite einen Stand noch nicht kennen kann — nach jedem
   *  Speichern aus dem Baustein heraus. */
  const [listeVersion, setListeVersion] = useState(0);

  /** Der geöffnete Artikel, aus der Trefferseite aufgelöst und danach festgehalten. */
  const [satz, setSatz] = useState<Artikel | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Muster aus Aufgabe 3).
   *
   * Der Meldungsstreifen ist ein Bearbeitungszustand ohne eigene Adresse: Bliebe er über
   * einen Menüwechsel hinweg stehen, käme man mit der Quittung einer längst verlassenen
   * Absicht zurück. Der offene Änderungsdialog braucht hier nichts — ihn hängt App.tsx mit
   * dem Detail aus (siehe den Kopf dieser Datei).
   *
   * Mit ihm fällt die FILTERLEISTE — Suchtext, Unternehmen, Kategorie, Blätterung —, denn
   * sie ist genauso ein Bearbeitungszustand ohne eigene Adresse (dieselbe Regel wie im
   * Rechnungsbereich, wo sie zuerst gezogen wurde). Bliebe sie stehen, zeigte die Liste
   * beim nächsten Betreten heimlich eine Teilmenge, ohne dass die Adresse es sagte: Wer
   * über das Menü zurückkommt, erwartet den Bereich, nicht den Rest einer alten Suche.
   * Der LADEFEHLER geht mit — ein Fehler von vorhin ist keine Auskunft über den Bestand
   * von jetzt.
   */
  useEffect(() => {
    if (aktiv) return;
    setHinweis(null);
    setSuche("");
    setCompanyId("");
    setKategorie("");
    setOffset(0);
    setLadeFehler(null);
  }, [aktiv]);

  const searchProducts = client.searchProducts;
  const listActiveTaxRates = client.listActiveTaxRates;

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    // Nur GESETZTE Filter gehen mit: Ein leerer Wert wäre ein Filter auf „leer".
    searchProducts({
      ...(companyId === "" ? {} : { companyId }),
      ...(kategorie === "" ? {} : { category: kategorie }),
      ...(suche === "" ? {} : { suche }),
      limit: JE_SEITE,
      offset,
    })
      .then((antwort) => { if (!abgebrochen) { setSeite(antwort); setLadeFehler(null); } })
      .catch(() => {
        if (!abgebrochen) {
          setLadeFehler("Der Artikelkatalog konnte nicht geladen werden. "
            + "Bitte die Seite neu laden.");
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, searchProducts, companyId, kategorie, suche, offset, listeVersion]);

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listActiveTaxRates()
      .then((antwort) => { if (!abgebrochen) setSteuersaetze(antwort.items); })
      .catch(() => {
        // Ein Fehlschlag hier macht den Bereich nicht unbrauchbar — nur der Name des
        // Steuersatzes fehlt dann und es steht der Rückfalltext da.
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listActiveTaxRates]);

  /*
   * Den geöffneten Artikel aus der Trefferseite auflösen.
   *
   * Ein FRISCHER Stand gewinnt immer — nur so zeigt das Detail nach dem Speichern den neuen
   * Preis, ohne dass der Bereich ein zweites Mal lesen müsste. Fehlt die Zeile dagegen in
   * der aktuellen Trefferseite (ein Filterwechsel hat sie hinausgedrängt), bleibt der zuvor
   * aufgelöste Satz stehen: Sonst risse ein Filterwechsel das Detail zu, das man gerade
   * liest — derselbe Grund wie beim `bezug` der Schwesterbereiche.
   */
  /*
   * Die Quittung gehört dem Artikel, über dem sie steht — also fällt sie mit der ADRESSE,
   * nicht mit dem Klick, der sie sonst allein gelöst hätte (Review-Befund Fixrunde 1).
   * Adressen wechseln auch ohne Zeilenklick: Zurück-Taste, Rückweg im Kopf des
   * Einspalters, ein Lesezeichen auf einen anderen Artikel. Bliebe sie dabei stehen,
   * meldete sie über dem NEUEN Artikel einen Erfolg, den es dort nie gab — dasselbe
   * Muster wie beim Dialog des Bausteins, der am `key` des Artikels hängt.
   */
  useEffect(() => { setHinweis(null); }, [artikelId]);

  useEffect(() => {
    if (artikelId === null) { setSatz(null); return; }
    const zeile = seite?.items.find((a) => a.id === artikelId);
    setSatz((alt) => {
      if (zeile !== undefined) return zeile;
      if (alt !== null && alt.id === artikelId) return alt;
      return null;
    });
  }, [artikelId, seite]);

  /** Filter ändern heisst: zurück auf die erste Seite. Sonst stünde man nach dem Filtern
   *  auf Seite 7 eines Ergebnisses mit einer Seite und sähe nichts. */
  function filtern(setzen: () => void): void {
    setzen();
    setOffset(0);
  }

  const gefiltert = companyId !== "" || kategorie !== "" || suche !== "";
  const von = seite === null || seite.total === 0 ? 0 : offset + 1;
  const bis = seite === null ? 0 : Math.min(offset + seite.items.length, seite.total);

  /* Wie in der Verwaltung: Die Kategorienliste kommt vom Server und hängt NICHT am
     Kategoriefilter — sonst enthielte sie nach der ersten Wahl nur noch sich selbst. Eine
     gewählte, aber (etwa nach Unternehmenswechsel) nicht mehr angebotene Kategorie bleibt
     trotzdem drin, damit die Auswahl nicht still umspringt. */
  const kategorien = seite === null ? []
    : seite.categories.includes(kategorie) || kategorie === ""
      ? seite.categories : [...seite.categories, kategorie];

  const spalte = (
    <div className="bart">
      {/*
        Der FILTERKOPF (components/Filterkopf.tsx) — Suchfeld, die zwei Zusatzfilter
        (Unternehmen, Kategorie) und für ADMINs der Verweis in die Verwaltung. Eine
        CHIP-Leiste hat dieser Bereich als einziger nicht: Ein Artikel hat keinen Status,
        und seine Kategorien stehen erst zur Laufzeit fest (siehe bereichsfilter.ts).

        `lage="spalte"` nimmt ihm seinen Kasten und legt die Zusatzfilter je auf eine ganze
        Zeile: In 280 Pixeln Spaltenbreite stehen zwei Auswahllisten nicht nebeneinander.
      */}
      <Filterkopf
        lage="spalte"
        suchLabel="Artikel durchsuchen"
        platzhalter="Name oder Artikelnummer"
        suche={suche}
        onSuche={(t) => filtern(() => setSuche(t))}
        {...(istAdmin ? { aktion: (
          <KiKnopf className="bart__pflege" onClick={p.onPflege}>
            Pflege in der Verwaltung
          </KiKnopf>
        ) } : {})}
        zusatz={
          <>
            {/* me-auswahl (ME-Etappe 4, P4). Der Optionstext gehört zum `key` (Befund P3-7:
                me-auswahl liest Optionen nur bei slotchange). */}
            <Auswahl id="bart-firma" name="bart-firma" className="bart__auswahl"
              beschriftung="Unternehmen" wert={companyId}
              onGeaendert={(e) => filtern(() => setCompanyId(e.wert))}>
              <option value="">alle</option>
              {companies.map((c) => (
                <option key={`${c.id}|${c.shortCode} · ${c.name}`} value={c.id}>
                  {c.shortCode} · {c.name}
                </option>
              ))}
            </Auswahl>
            <Auswahl id="bart-kategorie" name="bart-kategorie" className="bart__auswahl"
              beschriftung="Kategorie" wert={kategorie}
              onGeaendert={(e) => filtern(() => setKategorie(e.wert))}>
              <option value="">alle</option>
              {kategorien.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Auswahl>
          </>
        }
      />

      {/* Die ARTIKELLISTE. Sie ROLLT (siehe BereichArtikel.css): Der Filterkopf bleibt oben
          stehen; die Blätterung wandert mit dem Ende der Liste (sie steht IM Rollbereich) —
          wer weiterblättern will, ist ohnehin am Ende der Seite angekommen. */}
      <div className="bart__liste">
        {ladeFehler !== null && (
          <p className="bart__fehler" role="alert">{ladeFehler}</p>
        )}

        {ladeFehler === null && seite === null && (
          <p className="bart__lade" role="status">Lädt …</p>
        )}

        {ladeFehler === null && seite !== null && seite.items.length === 0 && (
          <p className="bart__hinweis">
            {gefiltert
              ? "Zu diesen Filtern gibt es keinen Artikel."
              : "Es sind noch keine Artikel hinterlegt."}
          </p>
        )}

        {/*
          Die Zeilen tragen den gemeinsamen Baustein (Entscheid Dirk 23.09.2026) — dieselbe
          Anatomie wie im Artikelstamm der Verwaltung, denn es ist derselbe Artikel.

          Zeile 2 nennt IMMER Preis und Kategorie (`artikelMeta`, geteilt mit der
          Verwaltung); der Beschreibungs-Anriss ist ERSATZLOS entfallen. Er stand ausserhalb
          des Zeilenknopfs, weil `renderMarkdown` Blockelemente und womöglich Links liefert —
          beides darf nicht in einen Knopf. Damit gehörte er sichtbar zur Zeile, ohne Teil
          ihres Ziels zu sein. Die Beschreibung steht vollständig im Lese-Detail rechts.

          Eine Marke „stillgelegt" braucht dieser Bereich nicht: Der Leseweg
          `GET /api/artikel` (Ruling P3B1B-G) gibt ausschliesslich AKTIVE Artikel heraus.
        */}
        <Objektliste label="Artikel">
          {(seite?.items ?? []).map((a) => (
            <Objektzeile
              key={a.id}
              name={a.name}
              kennung={a.productNumber}
              meta={artikelMeta(a.netUnitPrice, a.unit, a.category)}
              /* Die Abrechnungsquelle als ruhige Marke — nur, wenn gesetzt. */
              {...(quellenMarke(a.billingDataSource) === null
                ? {} : { marken: [quellenMarke(a.billingDataSource)!] })}
              gewaehlt={a.id === (artikelId ?? p.markiertId ?? null)}
              /* Der zugängliche Name nennt Nummer und Namen; zwanzig Zeilen „Öffnen" wären
                 für jemanden, der die Seite hört, nicht unterscheidbar. */
              ariaLabel={`Artikel ${a.productNumber}, ${a.name}, anzeigen`}
              /* Die Quittung räumt der Adress-Effekt oben ab, nicht dieser Klick: Sie fällt
                 dann auch bei den Adresswechseln, die diese Zeile gar nicht auslöst. */
              onOeffnen={() => p.onOeffnen(a.id)}
            />
          ))}
        </Objektliste>

        {seite !== null && seite.total > 0 && (
          <div className="bart__blaettern">
            <KiKnopf deaktiviert={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - JE_SEITE))}>
              Zurück
            </KiKnopf>
            <span className="bart__zusatz">{von}–{bis} von {seite.total}</span>
            <KiKnopf deaktiviert={offset + seite.items.length >= seite.total}
              onClick={() => setOffset((o) => o + JE_SEITE)}>
              Weiter
            </KiKnopf>
          </div>
        )}
      </div>
    </div>
  );

  /*
   * Die Detailfläche bekommt bewusst KEINE eigene Landmarke: Der Rahmen zeichnet dort bereits
   * eine (MeSchale), und eine zweite Region namens „Artikel" wäre mit der Spalte daneben
   * verwechselbar.
   */
  const detail = (
    <div className="bart__inhalt">
      {hinweis !== null && <p className="bart__meldung" role="status">{hinweis}</p>}

      {artikelId === null ? (
        <p className="bart__leer">
          Links einen Artikel wählen — er steht dann hier, mit allem, was
          auch der Katalog zeigte.
        </p>
      ) : satz === null ? (
        /*
         * Ohne Trefferzeile gibt es den Artikel nicht zu sehen — aber „nicht in der
         * Trefferliste" darf erst behauptet werden, wenn es eine gibt. Solange die Suche
         * läuft, wäre die Aussage schlicht falsch; und wenn sie gescheitert ist, liegt es
         * nicht am Artikel, sondern an der Liste.
         */
        ladeFehler !== null ? (
          <p className="bart__leer">
            Die Artikelliste konnte nicht geladen werden — ohne sie lässt sich dieser
            Artikel nicht öffnen. Bitte die Seite neu laden.
          </p>
        ) : seite === null ? (
          <p className="bart__leer">Lädt …</p>
        ) : (
          <p className="bart__leer">
            Dieser Artikel steht nicht in der Trefferliste — bitte über die Suche in der
            Spalte öffnen.
          </p>
        )
      ) : (
        <div className="bart__karte">
          <ArtikelDetail
            /* Eigene Identität je Artikel: Der Baustein führt den Änderungsdialog in
               eigenem Zustand. Ohne diesen Schlüssel nähme ein Adresswechsel den offenen
               Dialog mitsamt seinen Eingaben auf den NÄCHSTEN Artikel mit. */
            key={satz.id}
            client={client}
            artikel={satz}
            companies={companies}
            steuersaetze={steuersaetze}
            istAdmin={istAdmin}
            onGespeichert={() => {
              // Die Seite neu holen: Ein geänderter Name verschiebt die Zeile innerhalb
              // der Ordnung — das weiss nur der Server. Der frische Satz kommt von dort
              // zurück und ersetzt den gehaltenen (siehe der Auflöse-Effekt oben).
              setListeVersion((v) => v + 1);
              setHinweis("Die Änderungen sind gespeichert.");
            }}
          />
        </div>
      )}
    </div>
  );

  return { spalte, detail };
}
